import { useState } from "react";
import { Card } from "../../design-v2";
import InlineStepper from "./InlineStepper";
import SegmentedSelector from "./SegmentedSelector";
import { WORKOUT_PREFERENCE_DEFAULTS } from "../workoutLog/domain/workoutPreferenceDefaults";

const DEFAULTS = WORKOUT_PREFERENCE_DEFAULTS;

const WEIGHT_UNIT_OPTIONS = Object.freeze([
  { value: "LB", label: "lb" },
  { value: "KG", label: "kg" },
]);
const BARBELL_ENTRY_OPTIONS = Object.freeze([
  { value: "TOTAL", label: "Total Weight" },
  { value: "PER_SIDE", label: "Plates Per Side" },
]);
const REST_TIMER_OPTIONS = Object.freeze([
  { value: "AUTOMATIC", label: "Automatic" },
  { value: "MANUAL", label: "Manual" },
  { value: "OFF", label: "Off" },
]);
const REST_VALUES = Object.freeze(
  Array.from({ length: 20 }, (_value, index) => (index + 1) * 15)
);

function PreferenceHeading({ label, value, infoButton }) {
  return (
    <div className="lz-workout-preferences__heading">
      <div className="lz-workout-preferences__label-row">
        <h3 className="lz-workout-preferences__label">{label}</h3>
        {infoButton}
      </div>
      {value == null ? (
        <span className="lz-workout-preferences__not-set">Not set</span>
      ) : null}
    </div>
  );
}

function InfoGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="lz-v2-glyph"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10.75v5.5M12 7.5h.01" />
    </svg>
  );
}

function formatRestTime(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function standardBarWeight(unit) {
  return unit === "KG" ? 20 : 45;
}

function getOlympicBarWeightDescription(weightUnit) {
  if (weightUnit === "LB") {
    return "We recommend 45 lb. Some gyms also have lighter 35 lb bars.";
  }
  if (weightUnit === "KG") {
    return "We recommend 20 kg. Some gyms also have 15 kg bars.";
  }
  return "Select a Weight Unit to see the recommended bar weight.";
}

function getRestTimerDescription(restTimerMode) {
  if (restTimerMode === "AUTOMATIC") {
    return "Starts automatically after you complete a set or superset round.";
  }
  if (restTimerMode === "MANUAL") {
    return "Shows a timer control so you decide when rest starts.";
  }
  return null;
}

export default function WorkoutPreferencesScreen({
  preferences,
  onChange,
}) {
  const [showBarbellInfo, setShowBarbellInfo] = useState(false);
  const value = { ...DEFAULTS, ...(preferences || {}) };
  const customBarValue = value.olympicBarWeightValue == null
    ? null
    : Number(value.olympicBarWeightValue);
  const barUnit = value.olympicBarWeightUnit || value.weightUnit;
  const barDisplayValue = customBarValue ?? standardBarWeight(barUnit);
  const barBounds = barUnit === "KG"
    ? { min: 5, max: 35 }
    : { min: 10, max: 80 };
  const restTimerDescription = getRestTimerDescription(value.restTimerMode);

  function update(patch, options) {
    onChange(patch, options);
  }

  function updateBarWeight(nextValue) {
    if (!barUnit) {
      return;
    }
    update(
      {
        olympicBarWeightValue: String(nextValue),
        olympicBarWeightUnit: barUnit,
      },
      { debounceKey: "olympicBarWeight" }
    );
  }

  return (
    <div className="lz-workout-preferences">
      <p className="lz-settings-hint lz-workout-preferences__intro">
        Configure how workout logging and timing behave during your training sessions.
      </p>

      <Card padding="md" className="lz-workout-preferences__card">
        <section className="lz-workout-preferences__control" aria-labelledby="weight-unit-label">
          <div id="weight-unit-label">
            <PreferenceHeading label="Weight Unit" value={value.weightUnit} />
          </div>
          <SegmentedSelector
            label="Weight Unit"
            options={WEIGHT_UNIT_OPTIONS}
            value={value.weightUnit}
            onChange={(weightUnit) => update({ weightUnit })}
          />
        </section>

        <section className="lz-workout-preferences__control" aria-labelledby="barbell-entry-label">
          <div id="barbell-entry-label">
            <PreferenceHeading
              label="Barbell Entry"
              value={value.olympicBarbellConvention}
              infoButton={(
                <button
                  type="button"
                  className="lz-workout-preferences__info"
                  aria-label="About barbell entry"
                  aria-expanded={showBarbellInfo}
                  onClick={() => setShowBarbellInfo((current) => !current)}
                >
                  <InfoGlyph />
                </button>
              )}
            />
          </div>
          <SegmentedSelector
            label="Barbell Entry"
            options={BARBELL_ENTRY_OPTIONS}
            value={value.olympicBarbellConvention}
            onChange={(olympicBarbellConvention) => update({ olympicBarbellConvention })}
          />
          <p className="lz-workout-preferences__description">
            Choose how you normally enter Olympic barbell loads.
          </p>
          {showBarbellInfo ? (
            <p className="lz-workout-preferences__info-copy">
              Total Weight includes the bar. Plates Per Side records the plates loaded on one
              side and uses your Olympic bar weight for comparisons.
            </p>
          ) : null}
        </section>

        <div className="lz-workout-preferences__stepper">
          <InlineStepper
            label="Olympic Bar Weight"
            description={getOlympicBarWeightDescription(value.weightUnit)}
            value={barDisplayValue}
            valueText={barUnit ? String(barDisplayValue) : "45 lb / 20 kg"}
            suffix={barUnit ? barUnit.toLowerCase() : ""}
            min={barBounds.min}
            max={barBounds.max}
            step={1}
            disabled={!barUnit}
            onChange={updateBarWeight}
          />
        </div>
      </Card>

      <Card padding="md" className="lz-workout-preferences__card">
        <section className="lz-workout-preferences__control">
          <PreferenceHeading label="Rest Timer" value="set" />
          <SegmentedSelector
            label="Rest Timer"
            options={REST_TIMER_OPTIONS}
            value={value.restTimerMode}
            onChange={(restTimerMode) => update({ restTimerMode })}
          />
          {restTimerDescription ? (
            <p className="lz-workout-preferences__description">
              {restTimerDescription}
            </p>
          ) : null}
        </section>

        {value.restTimerMode !== "OFF" ? (
          <div className="lz-workout-preferences__stepper">
            <InlineStepper
              label="Default Rest Countdown"
              value={value.defaultRestSeconds}
              allowedValues={REST_VALUES}
              formatValue={formatRestTime}
              onChange={(defaultRestSeconds) => update(
                { defaultRestSeconds: Number(defaultRestSeconds) },
                { debounceKey: "defaultRestSeconds" }
              )}
            />
          </div>
        ) : null}
      </Card>

      <Card padding="md" className="lz-workout-preferences__card">
        <div className="lz-workout-preferences__switch-row">
          <div className="lz-workout-preferences__switch-copy">
            <h3 className="lz-workout-preferences__label">Keep Screen Awake</h3>
            <p className="lz-workout-preferences__description">
              Prevents the screen from sleeping during an active workout.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Keep Screen Awake"
            aria-checked={value.keepScreenAwake}
            className="lz-workout-preferences__switch"
            onClick={() => update({ keepScreenAwake: !value.keepScreenAwake })}
          >
            <span aria-hidden="true" className="lz-workout-preferences__switch-thumb" />
          </button>
        </div>
      </Card>

      <Card padding="md" className="lz-workout-preferences__card">
        <div className="lz-workout-preferences__switch-row">
          <div className="lz-workout-preferences__switch-copy">
            <h3 className="lz-workout-preferences__label">Previous performance</h3>
            <p className="lz-workout-preferences__description">
              Shows what you did last time, and a suggestion, while you log a workout.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Previous performance"
            aria-checked={value.previousPerformanceDefault !== false}
            className="lz-workout-preferences__switch"
            onClick={() => update({ previousPerformanceDefault: value.previousPerformanceDefault === false })}
          >
            <span aria-hidden="true" className="lz-workout-preferences__switch-thumb" />
          </button>
        </div>
      </Card>
    </div>
  );
}
